import { isAfter, isBefore, startOfDay, subMonths } from "date-fns";
import { useState, useMemo, useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Skeleton from "@mui/material/Skeleton";
import Chip from "@mui/material/Chip";
import ButtonMenu from "../../../components/Buttons/ButtonMenu";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import LoginIcon from "@mui/icons-material/Login";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { Star, Users } from "lucide-react";
import TableContainer from "../../../components/Tables/TableContainer";
import TableCell from "../../../pages/BillingAndPayments/components/TableCell";
import CancellationReasonModal from "../../../components/Modal/CancellationReasonModal";
import EditableAgencyName from "./EditableAgencyName";
import ModalAddAdvertiser from "./ModalAddAdvertiser";
import ModalAddCredits from "./ModalAddCredits";
import ModalSendClientEmail from "./ModalSendClientEmail";
import ModalModifyKpiCpa from "./ModalModifyKpiCpa";
import {
  mergeUsersForAgencyEmail,
  sortUsersByRolePriority,
} from "../utils/sortUsersByRolePriority";
import {
  TABLE_ADMIN_DASHBOARD_CLIENT,
  TABLE_ADMIN_DASHBOARD_RENEW_DATE,
  TABLE_ADMIN_DASHBOARD_RENEW_DATE_VALUE,
  TABLE_ADMIN_DASHBOARD_DISCOVERY_SOURCE,
  TABLE_ADMIN_DASHBOARD_CAMPAIGNS,
  TABLE_ADMIN_DASHBOARD_LAST_LOGIN,
  TABLE_ADMIN_DASHBOARD_LOGINS,
  TABLE_ADMIN_DASHBOARD_USERS,
  TABLE_ADMIN_DASHBOARD_AUTOMATION,
  TABLE_ADMIN_DASHBOARD_MRR,
  TABLE_ADMIN_DASHBOARD_PLAN,
  TABLE_ADMIN_DASHBOARD_LOADING_MORE,
  TABLE_ADMIN_DASHBOARD_SHOWING_CLIENTS,
  TABLE_ADMIN_CREATIVES_UTILIZED,
  TABLE_ADMIN_CREATIVE_UTILIZED,
  TABLE_ADMIN_DASHBOARD_CAMPAIGNS_ACTIVE,
  TABLE_ADMIN_LAST_LOGIN_DAYS,
  TABLE_ADMIN_DASHBOARD_NPS,
  TABLE_ADMIN_DASHBOARD_AI_CREATIVES_LAST_30_DAYS,
  TABLE_ADMIN_DASHBOARD_KPI,
  TABLE_ADMIN_CANCELLATION_REASON_BUTTON,
  TABLE_ADMIN_CANCELLED_BADGE,
  TABLE_ADMIN_DASHBOARD_SPEND,
  TABLE_ADMIN_DASHBOARD_CPA,
  TABLE_ADMIN_DASHBOARD_ROAS,
  TABLE_ADMIN_DASHBOARD_RESULTS,
  TABLE_ADMIN_DASHBOARD_TARGET_CPA,
  TABLE_ADMIN_ADD_ADVERTISER_BUTTON,
  TABLE_ADMIN_ADD_CREDITS_BUTTON,
  TABLE_ADMIN_ACTIONS_BUTTON,
  TABLE_ADMIN_CREDITS_USAGE,
  TABLE_ADMIN_PENDING_PAYMENT_BADGE,
  TABLE_ADMIN_PRIORITY_BADGE,
  TABLE_ADMIN_SEND_EMAIL_BUTTON,
  TABLE_ADMIN_LOGIN_AS_CLIENT_BUTTON,
  TABLE_ADMIN_MODIFY_KPI_CPA_BUTTON,
  OWNER_LOGIN_AS_ERROR_INVALID_EMAIL,
  ERROR,
  WITH_OUT_PLAN,
  ACTIVE,
  INACTIVE,
  TABLE_ADMIN_DASHBOARD_CAMPAIGNS_ACTIVE_SINGLE,
  TOTAL_ADVERTISERS_COUNT,
  TOTAL_ADVERTISER_COUNT,
  COLUMN_NAME_ACTIONS,
  TABLE_ADMIN_ACTIVATE_PLAN_BUTTON,
  TABLE_ADMIN_DEACTIVATE_PLAN_BUTTON,
  TABLE_ADMIN_CONFIRM_ACTIVATE_PLAN_TITLE,
  TABLE_ADMIN_CONFIRM_ACTIVATE_PLAN_MESSAGE,
  TABLE_ADMIN_CONFIRM_DEACTIVATE_PLAN_TITLE,
  TABLE_ADMIN_CONFIRM_DEACTIVATE_PLAN_MESSAGE,
  TABLE_ADMIN_MARK_DEMO_BUTTON,
  TABLE_ADMIN_CONFIRM_MARK_DEMO_TITLE,
  TABLE_ADMIN_CONFIRM_MARK_DEMO_MESSAGE,
  READY,
} from "../../../i18n/keysTranslations";
import { useTranslationApp } from "../../../lib/i18next";
import { useDispatchApp } from "../../../lib/redux";
import { getAnnotationUnreadPresentation } from "../utils/annotationUnread";
import AnnotationUnreadIndicator from "./AnnotationUnreadIndicator";
import {
  startMarkAgencyAdvertisersAsDemo,
  startSwitchAgencyIsPlanActive,
  startCreateOwnerLoginAsClientLink,
} from "../../../actions/owner";
import { buildOwnerLoginAsClientUrl } from "../../../actions/auth";
import { setShowBackdrop } from "../../../actions/ui";
import { REGEX_EMAIL } from "../../../utils/regex";
import { getOwnerLoginAsCreateErrorMessageKey } from "../../../utils/ownerLoginAsErrors";
import {
  getAdvertiserUserSubtitle,
  getPrimaryAdvertiserEmail,
} from "../utils/primaryAdvertiserUser";
import { ConfirmAlert, SimpleAlert } from "../../../components/Alerts/Alerts";
import {
  numberCompactFormat,
  numberDecimal,
  numberMetricFormat,
  isMeaningfulNumericMetricValue,
} from "../../../utils/numbers";
import {
  ALERT_ICON_TYPE_SUCCESS,
  ALERT_ICON_TYPE_ERROR,
  ALERT_COLORS,
  APPSUMO,
  PLAN_TYPE_CREDITS,
  RECURRENT,
  RECURRENT_TYPES,
  SIGNUP_DISCOVERY_SOURCE_OPTIONS,
} from "../../../utils/constants";
import {
  APPSUMO_CHIP_BG_COLOR,
  APPSUMO_CHIP_TEXT_COLOR,
  CREDITS_USAGE_HIGH_BG_COLOR,
  CREDITS_USAGE_HIGH_BORDER_COLOR,
  CREDITS_USAGE_HIGH_TEXT_COLOR,
  CREDITS_USAGE_LOW_BG_COLOR,
  CREDITS_USAGE_LOW_BORDER_COLOR,
  CREDITS_USAGE_LOW_TEXT_COLOR,
  PENDING_PAYMENT_BADGE_BG_COLOR,
  PENDING_PAYMENT_BADGE_BORDER_COLOR,
  PENDING_PAYMENT_BADGE_TEXT_COLOR,
  PRIORITY_BADGE_BG_COLOR,
  PRIORITY_BADGE_BORDER_COLOR,
  PRIORITY_BADGE_TEXT_COLOR,
  RECURRENT_BADGE_MONTHLY_BG_COLOR,
  RECURRENT_BADGE_MONTHLY_BORDER_COLOR,
  RECURRENT_BADGE_MONTHLY_TEXT_COLOR,
  RECURRENT_BADGE_YEARLY_BG_COLOR,
  RECURRENT_BADGE_YEARLY_BORDER_COLOR,
  RECURRENT_BADGE_YEARLY_TEXT_COLOR,
} from "../../../utils/colors";
import _ from "lodash";

// Common props for TableCell components
const commonPropsTableCellHeader = {
  showTooltip: false,
  isHeader: true,
};

const commonPropsTableCellBody = {
  showTooltip: true,
  isHeader: false,
  showLastRowBorder: true,
};

const commonSx = {
  width: "120px",
  minWidth: "120px",
};

const CLIENTS_TABLE_ROW_TYPES = {
  SOLO_ADVERTISER: "soloAdvertiser",
  AGENCY_ADVERTISER: "agencyAdvertiser",
};

const CampaignAlertsErrorIndicator = ({ alerts = [] }) => {
  if (!Array.isArray(alerts) || alerts.length === 0) {
    return null;
  }

  return (
    <Tooltip
      title={
        <Box>
          {alerts.map((alert, index) => {
            const platformName = alert?.platform
              ? alert.platform.charAt(0).toUpperCase() + alert.platform.slice(1)
              : "";
            const message = alert?.shortMessage || alert?.longMessage || "";
            if (!message) {
              return null;
            }
            return (
              <Typography
                key={`${alert?.platform || "platform"}-${alert?.alertType || "type"}-${index}`}
                sx={{ fontSize: "0.875rem", color: "white" }}
                variant="body2"
              >
                {platformName ? `${platformName}: ${message}` : message}
              </Typography>
            );
          })}
        </Box>
      }
      arrow
    >
      <Box
        sx={{
          width: 18,
          height: 18,
          borderRadius: "50%",
          backgroundColor: ALERT_COLORS.error.background,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Typography
          component="span"
          sx={{
            color: "#ffffff",
            fontSize: "0.75rem",
            fontWeight: 700,
            lineHeight: 1,
          }}
        >
          !
        </Typography>
      </Box>
    </Tooltip>
  );
};

const AppSumoChip = () => (
  <Chip
    label={APPSUMO}
    size="small"
    sx={{
      height: "18px",
      borderRadius: "6px",
      px: 0.25,
      backgroundColor: APPSUMO_CHIP_BG_COLOR,
      color: APPSUMO_CHIP_TEXT_COLOR,
      "& .MuiChip-label": {
        px: 0.75,
        py: 0.25,
        fontSize: "10px",
        fontWeight: 600,
        lineHeight: 1,
      },
    }}
  />
);

const PriorityChip = ({ t }) => (
  <Typography
    component="span"
    variant="caption"
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.5,
      fontSize: "10px",
      fontWeight: 500,
      px: 0.75,
      py: 0.25,
      borderRadius: "6px",
      border: "1px solid",
      lineHeight: 1.2,
      color: PRIORITY_BADGE_TEXT_COLOR,
      backgroundColor: PRIORITY_BADGE_BG_COLOR,
      borderColor: PRIORITY_BADGE_BORDER_COLOR,
    }}
  >
    {t(TABLE_ADMIN_PRIORITY_BADGE)}
  </Typography>
);

const AddOnSpendFeeStar = () => (
  <Box
    component="span"
    aria-hidden="true"
    sx={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: "18px",
      height: "18px",
      borderRadius: "50%",
      background: "linear-gradient(135deg, #fcd34d 0%, #f59e0b 100%)",
      boxShadow: "0 1px 2px rgba(0, 0, 0, 0.15)",
    }}
  >
    <Star size={10} strokeWidth={2} fill="#fff" color="#fff" />
  </Box>
);

const getDiscoverySourceDisplay = ({ discoverySourceKey, t }) => {
  if (!discoverySourceKey || String(discoverySourceKey).trim() === "") {
    return "-";
  }
  const option = SIGNUP_DISCOVERY_SOURCE_OPTIONS.find(
    (o) => o.value === discoverySourceKey
  );
  return option ? t(option.labelKey) : "-";
};

const getNormalizedCurrencyCode = (currency) => {
  if (typeof currency !== "string" || !currency.trim()) {
    return null;
  }

  return currency.trim().toUpperCase();
};

const resolveCpaByPlatformRow = ({ cpaByPlatform, platform }) => {
  if (
    !cpaByPlatform ||
    typeof cpaByPlatform !== "object" ||
    platform === null ||
    platform === undefined ||
    platform === ""
  ) {
    return null;
  }

  const p = String(platform);
  const direct = cpaByPlatform[p];
  if (direct && typeof direct === "object") {
    return direct;
  }

  const lower = p.toLowerCase();
  const lowerHit = cpaByPlatform[lower];
  if (lowerHit && typeof lowerHit === "object") {
    return lowerHit;
  }

  const matchKey = Object.keys(cpaByPlatform).find(
    (k) => k.toLowerCase() === lower
  );
  const hit = matchKey != null ? cpaByPlatform[matchKey] : null;
  return hit && typeof hit === "object" ? hit : null;
};

const getCpaValueFromCurrencyRow = ({ row, normalizedCurrency }) => {
  if (!row || typeof row !== "object" || !normalizedCurrency) {
    return null;
  }

  const direct = row[normalizedCurrency];
  if (isMeaningfulNumericMetricValue(direct)) {
    return Number(direct);
  }

  const key = Object.keys(row).find(
    (k) => getNormalizedCurrencyCode(k) === normalizedCurrency
  );
  const v = key != null ? row[key] : null;
  return isMeaningfulNumericMetricValue(v) ? Number(v) : null;
};

const getTotalCpaForCurrency = ({ totalsCpa, normalizedCurrency }) => {
  if (!totalsCpa || typeof totalsCpa !== "object" || !normalizedCurrency) {
    return null;
  }

  return getCpaValueFromCurrencyRow({
    row: totalsCpa,
    normalizedCurrency,
  });
};

const getRealCpaForTargetPart = ({ part, totalsCpa, cpaByPlatform }) => {
  const normalizedCurrency = getNormalizedCurrencyCode(part?.currency);
  if (!normalizedCurrency) {
    return null;
  }

  if (part?.platform) {
    const platformRow = resolveCpaByPlatformRow({
      cpaByPlatform,
      platform: part.platform,
    });
    return getCpaValueFromCurrencyRow({
      row: platformRow,
      normalizedCurrency,
    });
  }

  return getTotalCpaForCurrency({ totalsCpa, normalizedCurrency });
};

/**
 * Maps normalized currency code -> true when displayed CPA for that currency
 * exceeds an applicable target (aggregate or per-platform manual).
 */
const getCpaCurrencyExceedanceByCurrency = ({ kpiSummary, metricsTotals }) => {
  const exceeded = {};
  const parts = Array.isArray(kpiSummary?.targetCpaParts)
    ? kpiSummary.targetCpaParts
    : [];
  const totalsCpa = metricsTotals?.cpa;
  const cpaByPlatform = metricsTotals?.cpaByPlatform;

  for (const part of parts) {
    const targetCpa = Number(part?.targetCpa);
    if (!Number.isFinite(targetCpa) || targetCpa <= 0) {
      continue;
    }

    const currency = getNormalizedCurrencyCode(part?.currency);
    if (!currency) {
      continue;
    }

    const realCpa = getRealCpaForTargetPart({
      part,
      totalsCpa,
      cpaByPlatform,
    });
    if (!isMeaningfulNumericMetricValue(realCpa)) {
      continue;
    }

    if (realCpa > targetCpa) {
      exceeded[currency] = true;
    }
  }

  const legacyTargetCpa = Number(kpiSummary?.targetCpa);
  const legacyCurrency = getNormalizedCurrencyCode(
    kpiSummary?.targetCpaCurrency
  );
  if (
    Number.isFinite(legacyTargetCpa) &&
    legacyTargetCpa > 0 &&
    legacyCurrency
  ) {
    const legacyReal = getTotalCpaForCurrency({
      totalsCpa,
      normalizedCurrency: legacyCurrency,
    });
    if (
      isMeaningfulNumericMetricValue(legacyReal) &&
      legacyReal > legacyTargetCpa
    ) {
      exceeded[legacyCurrency] = true;
    }
  }

  return exceeded;
};

const getKpiCellValue = ({ kpiSummary, t }) => {
  const primaryKpi = kpiSummary?.primaryKpi;
  const parts = kpiSummary?.targetCpaParts;

  const hasValidPart =
    Array.isArray(parts) &&
    parts.some(
      (p) =>
        typeof p?.targetCpa === "number" &&
        Number.isFinite(p.targetCpa) &&
        p.targetCpa > 0
    );

  const hasLegacyTargetCpa =
    typeof kpiSummary?.targetCpa === "number" &&
    Number.isFinite(kpiSummary.targetCpa) &&
    kpiSummary.targetCpa > 0;

  const hasTargetCpa = hasValidPart || hasLegacyTargetCpa;

  if (!primaryKpi && !hasTargetCpa) {
    return null;
  }

  const formatAmount = (amount) => numberMetricFormat(numberDecimal(amount));

  /** One line per platform / bucket when targetCpaParts is used (manual + derived). */
  let targetCpaLines = null;
  let targetCpaLabel = null;
  if (hasTargetCpa) {
    if (hasValidPart) {
      const segments = parts
        .filter(
          (p) =>
            typeof p?.targetCpa === "number" &&
            Number.isFinite(p.targetCpa) &&
            p.targetCpa > 0
        )
        .map((p) => {
          const amountStr = p?.currency
            ? `${formatAmount(p.targetCpa)} ${p.currency}`
            : formatAmount(p.targetCpa);
          if (p?.platform) {
            return `${t(p.platform)}: ${amountStr}`;
          }
          return amountStr;
        });
      if (segments.length > 0) {
        targetCpaLines = segments;
      }
    } else if (hasLegacyTargetCpa) {
      const amountStr = formatAmount(kpiSummary.targetCpa);
      const suffix = kpiSummary.targetCpaCurrency
        ? `${amountStr} ${kpiSummary.targetCpaCurrency}`
        : amountStr;
      targetCpaLabel = `${t(TABLE_ADMIN_DASHBOARD_TARGET_CPA)}: ${suffix}`;
    }
  }

  return {
    primaryKpi: t(primaryKpi) || "-",
    targetCpaLines,
    targetCpaLabel,
  };
};

const RecurrentBadge = ({ recurrent, t }) => {
  const recurrentKey = RECURRENT[recurrent] ? recurrent : "monthly";

  const badgeColor =
    recurrentKey === RECURRENT_TYPES.yearly
      ? {
          color: RECURRENT_BADGE_YEARLY_TEXT_COLOR,
          backgroundColor: RECURRENT_BADGE_YEARLY_BG_COLOR,
          borderColor: RECURRENT_BADGE_YEARLY_BORDER_COLOR,
        }
      : {
          color: RECURRENT_BADGE_MONTHLY_TEXT_COLOR,
          backgroundColor: RECURRENT_BADGE_MONTHLY_BG_COLOR,
          borderColor: RECURRENT_BADGE_MONTHLY_BORDER_COLOR,
        };

  return (
    <Typography
      component="span"
      variant="caption"
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        fontSize: "10px",
        fontWeight: 500,
        px: 0.75,
        py: 0.25,
        borderRadius: "6px",
        border: "1px solid",
        lineHeight: 1.2,
        ...badgeColor,
      }}
    >
      <CalendarTodayOutlinedIcon
        sx={{ fontSize: "0.625rem", color: "inherit" }}
      />
      {t(RECURRENT[recurrentKey])}
    </Typography>
  );
};

const PendingPaymentBadge = ({ t }) => (
  <Typography
    component="span"
    variant="caption"
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.5,
      fontSize: "10px",
      fontWeight: 500,
      px: 0.75,
      py: 0.25,
      borderRadius: "6px",
      border: "1px solid",
      lineHeight: 1.2,
      color: PENDING_PAYMENT_BADGE_TEXT_COLOR,
      backgroundColor: PENDING_PAYMENT_BADGE_BG_COLOR,
      borderColor: PENDING_PAYMENT_BADGE_BORDER_COLOR,
    }}
  >
    <PaymentsOutlinedIcon sx={{ fontSize: "0.625rem", color: "inherit" }} />
    {t(TABLE_ADMIN_PENDING_PAYMENT_BADGE)}
  </Typography>
);

const CreditsUsageMiniBar = ({ subscriptionPlanInfo, t }) => {
  const totalCredits = subscriptionPlanInfo?.benefits?.credits;
  const remainingCredits = subscriptionPlanInfo?.usage?.credits;

  if (totalCredits == null || remainingCredits == null || totalCredits <= 0) {
    return null;
  }

  const usedCredits = totalCredits - remainingCredits;
  const boundedUsedCredits = Math.max(0, Math.min(usedCredits, totalCredits));
  const hasUsedCredits = boundedUsedCredits > 0;

  const colorByUsage = hasUsedCredits
    ? {
        color: CREDITS_USAGE_LOW_TEXT_COLOR,
        backgroundColor: CREDITS_USAGE_LOW_BG_COLOR,
        borderColor: CREDITS_USAGE_LOW_BORDER_COLOR,
      }
    : {
        color: CREDITS_USAGE_HIGH_TEXT_COLOR,
        backgroundColor: CREDITS_USAGE_HIGH_BG_COLOR,
        borderColor: CREDITS_USAGE_HIGH_BORDER_COLOR,
      };

  return (
    <Typography
      component="span"
      variant="caption"
      sx={{
        fontSize: "10px",
        fontWeight: 500,
        px: 0.75,
        py: 0.25,
        borderRadius: "6px",
        border: "1px solid",
        lineHeight: 1.2,
        ...colorByUsage,
      }}
    >
      {t(TABLE_ADMIN_CREDITS_USAGE, {
        used: boundedUsedCredits,
        total: totalCredits,
      })}
    </Typography>
  );
};

const AdvertiserDetailsRow = ({
  recurrent,
  subscriptionPlanInfo,
  typeOfPlans,
  t,
  sx,
}) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 0.75,
      mt: 0.6,
      ...sx,
    }}
  >
    <RecurrentBadge recurrent={recurrent} t={t} />
    {typeOfPlans === PLAN_TYPE_CREDITS ? (
      <CreditsUsageMiniBar subscriptionPlanInfo={subscriptionPlanInfo} t={t} />
    ) : null}
    <Box />
  </Box>
);

const CancelledBadge = ({ t }) => (
  <Box
    component="span"
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.5,
      px: 0.75,
      py: 0.25,
      borderRadius: "6px",
      border: "1px solid",
      color: PENDING_PAYMENT_BADGE_TEXT_COLOR,
      backgroundColor: PENDING_PAYMENT_BADGE_BG_COLOR,
      borderColor: PENDING_PAYMENT_BADGE_BORDER_COLOR,
    }}
  >
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        lineHeight: 0,
      }}
    >
      <CancelOutlinedIcon sx={{ fontSize: "0.8rem", color: "inherit" }} />
    </Box>
    <Typography
      component="span"
      variant="caption"
      sx={{
        fontSize: "10px",
        fontWeight: 500,
        lineHeight: 1,
        color: "inherit",
      }}
    >
      {t(TABLE_ADMIN_CANCELLED_BADGE)}
    </Typography>
  </Box>
);

const ViewCancellationReasonBadge = ({ onClick, t }) => (
  <Box
    component="span"
    role="button"
    tabIndex={0}
    onClick={(event) => {
      event.stopPropagation();
      onClick();
    }}
    onKeyDown={(event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        event.stopPropagation();
        onClick();
      }
    }}
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.5,
      px: 0.75,
      py: 0.25,
      borderRadius: "6px",
      border: "1px solid #E5E7EB",
      color: "#374151",
      backgroundColor: "#F9FAFB",
      cursor: "pointer",
      "&:hover": {
        backgroundColor: "#F3F4F6",
      },
    }}
  >
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        lineHeight: 0,
      }}
    >
      <VisibilityOutlinedIcon sx={{ fontSize: "0.85rem", color: "inherit" }} />
    </Box>
    <Typography
      variant="caption"
      sx={{
        fontSize: "10px",
        fontWeight: 500,
        lineHeight: 1,
        color: "inherit",
        mb: 0.2,
      }}
    >
      {t(TABLE_ADMIN_CANCELLATION_REASON_BUTTON)}
    </Typography>
  </Box>
);

const CancellationDetailsRow = ({
  cancellationData,
  businessName,
  onOpenModal,
  t,
  sx,
}) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 0.75,
      mt: 0.6,
      ...sx,
    }}
  >
    <CancelledBadge t={t} />
    <ViewCancellationReasonBadge
      onClick={() => onOpenModal(cancellationData, businessName)}
      t={t}
    />
  </Box>
);

const LoginAsClientIconButton = ({
  isLoading = false,
  disabled = false,
  onClick,
  ariaLabel,
}) => (
  <IconButton
    size="small"
    onClick={onClick}
    disabled={disabled || isLoading}
    aria-label={ariaLabel}
    sx={{
      padding: "4px",
      color: "#6b7280",
      "&:hover": {
        color: "#374151",
        backgroundColor: "rgba(0, 0, 0, 0.04)",
      },
    }}
  >
    {isLoading ? (
      <CircularProgress size={14} thickness={4} />
    ) : (
      <LoginIcon sx={{ fontSize: "1rem" }} />
    )}
  </IconButton>
);

const hasCancellationData = (advertiser) =>
  Boolean(advertiser?.cancellationData);

const hasSubscriptionPlanRow = (advertiser) =>
  Boolean(
    advertiser?.subscriptionPlan &&
      advertiser.subscriptionPlan !== WITH_OUT_PLAN
  );

const formatRenewDateCell = ({ renewDate, t }) => {
  if (!renewDate) return "-";
  return t(TABLE_ADMIN_DASHBOARD_RENEW_DATE_VALUE, {
    date: new Date(renewDate),
  });
};

const isRenewDatePast = (renewDate) => {
  if (!renewDate) return false;
  return isBefore(startOfDay(new Date(renewDate)), startOfDay(new Date()));
};

const isPriorityClient = (creationTime) => {
  if (creationTime == null || Number.isNaN(Number(creationTime))) {
    return false;
  }
  return isAfter(new Date(Number(creationTime)), subMonths(new Date(), 3));
};

const getRenewDateTypographySx = ({ renewDate }) => {
  const isPast = isRenewDatePast(renewDate);
  return {
    color: isPast ? "#ef4444" : "#374151",
    fontWeight: isPast ? 500 : 400,
  };
};

const getAgencyBadgeDetailsFromAdvertisers = ({ advertisers }) => {
  const list = advertisers || [];
  const representative =
    list.find((c) => c?.advertiser?.typeOfPlans === PLAN_TYPE_CREDITS) ||
    list.find((c) => hasSubscriptionPlanRow(c?.advertiser)) ||
    list[0];

  const advertiser = representative?.advertiser;
  if (!advertiser) return null;

  return {
    recurrent:
      advertiser.recurrent ||
      advertiser.subscriptionPlanInfo?.recurrent ||
      "monthly",
    subscriptionPlanInfo: advertiser.subscriptionPlanInfo,
    typeOfPlans: representative.typeOfPlans ?? advertiser.typeOfPlans,
  };
};

const getAgencyCancellationFromAdvertisers = ({ advertisers }) => {
  const list = advertisers || [];
  const representative =
    list.find((c) => hasSubscriptionPlanRow(c?.advertiser)) || list[0];
  return representative?.advertiser?.cancellationData || null;
};

const mergeSpendTotalsByCurrency = ({ advertisers }) => {
  const merged = {};
  for (const ta of advertisers) {
    const spend = ta.metrics?.totals?.spend;
    if (!spend || typeof spend !== "object") continue;
    for (const [currency, amount] of Object.entries(spend)) {
      if (!isMeaningfulNumericMetricValue(amount)) continue;
      const n = Number(amount);
      merged[currency] = (merged[currency] || 0) + n;
    }
  }
  return _.isEmpty(merged) ? null : merged;
};

const averageCpaTotalsByCurrency = ({ advertisers }) => {
  const sums = {};
  const counts = {};
  for (const ta of advertisers) {
    const cpa = ta.metrics?.totals?.cpa;
    if (!cpa || typeof cpa !== "object") continue;
    for (const [currency, amount] of Object.entries(cpa)) {
      if (!isMeaningfulNumericMetricValue(amount)) continue;
      const n = Number(amount);
      sums[currency] = (sums[currency] || 0) + n;
      counts[currency] = (counts[currency] || 0) + 1;
    }
  }
  const out = {};
  for (const c of Object.keys(sums)) {
    const cnt = counts[c];
    if (cnt > 0) out[c] = sums[c] / cnt;
  }
  return _.isEmpty(out) ? null : out;
};

const averageRoasAcrossAdvertisers = ({ advertisers }) => {
  let sum = 0;
  let count = 0;
  for (const ta of advertisers) {
    const r = ta.metrics?.totals?.roas;
    if (!isMeaningfulNumericMetricValue(r)) continue;
    sum += Number(r);
    count += 1;
  }
  if (count === 0) return null;
  return sum / count;
};

const sumMainActionResults = ({ advertisers }) => {
  let sum = 0;
  let any = false;
  for (const ta of advertisers) {
    const v = ta.metrics?.totals?.mainAction;
    if (typeof v === "number" && !Number.isNaN(v)) {
      sum += v;
      any = true;
    }
  }
  return any ? sum : null;
};

const ClientsTable = ({
  isLoading,
  displayedClients,
  groupedClients,
  hasMoreItems,
  isLoadingMore,
  lastItemRef,
  totalClients,
  onSelectAnnotationTarget = () => {},
  annotationReadStateByScope = {},
}) => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const tableWrapperRef = useRef(null);
  const tableHeadRef = useRef(null);
  const lastStickyTopRef = useRef(0);

  // State for add advertiser modal
  const [addAdvertiserModalOpen, setAddAdvertiserModalOpen] = useState(false);
  const [addAdvertiserAgencyID, setAddAdvertiserAgencyID] = useState(null);
  const [addCreditsModalOpen, setAddCreditsModalOpen] = useState(false);
  const [addCreditsAgencyID, setAddCreditsAgencyID] = useState(null);
  const [sendEmailModalOpen, setSendEmailModalOpen] = useState(false);
  const [sendEmailUsers, setSendEmailUsers] = useState([]);
  const [sendEmailContextTitle, setSendEmailContextTitle] = useState("");

  const [modifyKpiCpaModalOpen, setModifyKpiCpaModalOpen] = useState(false);
  const [modifyKpiCpaModalClient, setModifyKpiCpaModalClient] = useState(null);

  // State for cancellation modal
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCancellationData, setSelectedCancellationData] =
    useState(null);
  const [selectedBusinessName, setSelectedBusinessName] = useState("");

  const [planSwitchLoadingAgencyID, setPlanSwitchLoadingAgencyID] =
    useState(null);
  const [markDemoLoadingAgencyID, setMarkDemoLoadingAgencyID] = useState(null);
  const [loginAsClientLoadingId, setLoginAsClientLoadingId] = useState(null);
  const agencyActionsBusyAgencyID =
    planSwitchLoadingAgencyID || markDemoLoadingAgencyID;

  // State for expanded agencies
  const [expandedAgencies, setExpandedAgencies] = useState(new Set());

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const onUpdateStickyTop = () => {
      const appBarElement = document.querySelector(".MuiAppBar-root");
      const tableElement = tableWrapperRef.current;
      const tableHeadElement = tableHeadRef.current;

      if (!appBarElement || !tableElement || !tableHeadElement) {
        return;
      }

      const navbarBottom = appBarElement.getBoundingClientRect().bottom;
      const tableTop = tableElement.getBoundingClientRect().top;
      const nextTop = Math.max(0, Math.round(navbarBottom - tableTop));
      if (lastStickyTopRef.current === nextTop) return;
      lastStickyTopRef.current = nextTop;
      tableHeadElement.style.top = `${nextTop}px`;
    };

    onUpdateStickyTop();
    window.addEventListener("scroll", onUpdateStickyTop, {
      passive: true,
    });
    window.addEventListener("resize", onUpdateStickyTop);

    return () => {
      window.removeEventListener("scroll", onUpdateStickyTop);
      window.removeEventListener("resize", onUpdateStickyTop);
    };
  }, []);

  const handleToggleAgency = (agencyID) => {
    setExpandedAgencies((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(agencyID)) {
        newSet.delete(agencyID);
      } else {
        newSet.add(agencyID);
      }
      return newSet;
    });
  };

  const handleOpenCancellationModal = (cancellationData, businessName) => {
    setSelectedCancellationData(cancellationData);
    setSelectedBusinessName(businessName);
    setModalOpen(true);
  };

  const handleCloseCancellationModal = () => {
    setModalOpen(false);
    setSelectedCancellationData(null);
    setSelectedBusinessName("");
  };

  const onOpenAddAdvertiserModal = (agencyID) => {
    setAddAdvertiserAgencyID(agencyID);
    setAddAdvertiserModalOpen(true);
  };

  const onCloseAddAdvertiserModal = () => {
    setAddAdvertiserModalOpen(false);
    setAddAdvertiserAgencyID(null);
  };

  const onOpenAddCreditsModal = (agencyID) => {
    setAddCreditsAgencyID(agencyID);
    setAddCreditsModalOpen(true);
  };

  const onCloseAddCreditsModal = () => {
    setAddCreditsModalOpen(false);
    setAddCreditsAgencyID(null);
  };

  const onOpenSendEmailModal = ({ users, contextTitle }) => {
    setSendEmailUsers(Array.isArray(users) ? users : []);
    setSendEmailContextTitle(contextTitle || "");
    setSendEmailModalOpen(true);
  };

  const onCloseSendEmailModal = () => {
    setSendEmailModalOpen(false);
    setSendEmailUsers([]);
    setSendEmailContextTitle("");
  };

  const onOpenModifyKpiCpaModal = ({ client }) => {
    setModifyKpiCpaModalClient(client);
    setModifyKpiCpaModalOpen(true);
  };

  const onCloseModifyKpiCpaModal = () => {
    setModifyKpiCpaModalOpen(false);
    setModifyKpiCpaModalClient(null);
  };

  const hasUsersWithEmail = (users) =>
    Array.isArray(users) &&
    users.some((u) => u?.email && String(u.email).trim() !== "");

  const onSwitchAgencyPlanActive = async ({ agencyID, isDeactivation }) => {
    if (!agencyID || agencyActionsBusyAgencyID) return;

    const confirmResult = await ConfirmAlert({
      title: t(
        isDeactivation
          ? TABLE_ADMIN_CONFIRM_DEACTIVATE_PLAN_TITLE
          : TABLE_ADMIN_CONFIRM_ACTIVATE_PLAN_TITLE
      ),
      text: t(
        isDeactivation
          ? TABLE_ADMIN_CONFIRM_DEACTIVATE_PLAN_MESSAGE
          : TABLE_ADMIN_CONFIRM_ACTIVATE_PLAN_MESSAGE
      ),
    });
    if (!confirmResult.isConfirmed) return;

    setPlanSwitchLoadingAgencyID(agencyID);
    try {
      const result = await dispatch(startSwitchAgencyIsPlanActive(agencyID));
      if (result?.ok) {
        SimpleAlert({
          title: t(READY),
          icon: ALERT_ICON_TYPE_SUCCESS,
        });
      }
    } finally {
      setPlanSwitchLoadingAgencyID(null);
    }
  };

  const onMarkAgencyAsDemo = async ({ agencyID }) => {
    if (!agencyID || agencyActionsBusyAgencyID) return;

    const confirmResult = await ConfirmAlert({
      title: t(TABLE_ADMIN_CONFIRM_MARK_DEMO_TITLE),
      text: t(TABLE_ADMIN_CONFIRM_MARK_DEMO_MESSAGE),
    });
    if (!confirmResult.isConfirmed) return;

    setMarkDemoLoadingAgencyID(agencyID);
    try {
      const result = await dispatch(startMarkAgencyAdvertisersAsDemo(agencyID));
      if (result?.ok) {
        SimpleAlert({
          title: t(READY),
          icon: ALERT_ICON_TYPE_SUCCESS,
        });
      }
    } finally {
      setMarkDemoLoadingAgencyID(null);
    }
  };

  const isValidAgencyID = (agencyID) =>
    agencyID && agencyID !== "unknown" && agencyID.trim() !== "";

  const agencyGroupHasCreditsPlan = (agencyGroup) =>
    (agencyGroup?.advertisers || []).some(
      (item) => item?.advertiser?.typeOfPlans === PLAN_TYPE_CREDITS
    );

  // Transform grouped clients to include transformed data
  const transformedGroupedClients = useMemo(() => {
    if (!groupedClients || groupedClients.length === 0) return [];

    return groupedClients.map((agencyGroup) => {
      const transformedAdvertisers = agencyGroup.advertisers.map((client) => {
        const { id, advertiser } = client;

        const activeCampaignsCount = advertiser.campaigns?.activeCampaigns || 0;
        const totalCampaignsCount = advertiser.campaigns?.totalCampaigns || 0;
        const appliedOptimizationsCount =
          advertiser.optimizations?.appliedOptimizations || 0;
        const activeUsersCount = advertiser.activeUsers || 0;
        const totalLoginsLast30Days =
          advertiser.loginMetrics?.totalLoginsLast30Days || 0;
        const daysSinceLastLogin =
          advertiser.loginMetrics?.daysSinceLastLogin ?? null;

        const healthScore = advertiser.isLoading
          ? null
          : (advertiser.healthScore ?? null);

        return {
          id,
          name: advertiser.businessName,
          searchMatchedEmail: advertiser.searchMatchedEmail ?? null,
          isLoading: advertiser.isLoading,
          hasRedCampaignAlerts: advertiser.hasRedCampaignAlerts || false,
          redCampaignAlerts: advertiser.redCampaignAlerts || [],
          kpiSummary:
            advertiser.kpiSummary || advertiser.campaigns?.kpiSummary || null,
          healthScore,
          activeCampaigns: activeCampaignsCount,
          totalCampaigns: totalCampaignsCount,
          metrics: advertiser.metrics,
          lastLogin: daysSinceLastLogin,
          totalLoginsLast30Days,
          activeUsers: activeUsersCount,
          optimizationsApplied: appliedOptimizationsCount,
          npsRating: advertiser.npsRating || 0,
          invoiceStatus:
            advertiser.isPlanActive === true
              ? "paid"
              : advertiser.isPlanActive === false
                ? "overdue"
                : "pending",
          mrr: advertiser.subscriptionPlanInfo?.price || 0,
          aiCreativesLast30Days: advertiser.aiCreativesLast30Days || 0,
          typeOfPlans: advertiser.typeOfPlans,
          advertiser,
        };
      });

      let agencyAverageHealthScore = null;
      let isAgencyHealthLoading = false;

      if (transformedAdvertisers.length > 1) {
        let sum = 0;
        for (const ta of transformedAdvertisers) {
          if (ta.isLoading || ta.healthScore === null) {
            isAgencyHealthLoading = true;
            agencyAverageHealthScore = null;
            break;
          }
          sum += ta.healthScore;
        }
        if (!isAgencyHealthLoading) {
          agencyAverageHealthScore = Math.round(
            sum / transformedAdvertisers.length
          );
        }
      }

      let agencyRowMetrics = null;
      let agencyBadgeDetails = null;
      let agencyCancellationData = null;
      if (transformedAdvertisers.length > 1) {
        const isAgencyMetricsLoading = transformedAdvertisers.some(
          (ta) => ta.isLoading
        );
        if (isAgencyMetricsLoading) {
          agencyRowMetrics = { isLoading: true };
        } else {
          const first = transformedAdvertisers[0];
          agencyRowMetrics = {
            isLoading: false,
            spendByCurrency: mergeSpendTotalsByCurrency({
              advertisers: transformedAdvertisers,
            }),
            cpaAverageByCurrency: averageCpaTotalsByCurrency({
              advertisers: transformedAdvertisers,
            }),
            roasAverage: averageRoasAcrossAdvertisers({
              advertisers: transformedAdvertisers,
            }),
            sumCampaigns: transformedAdvertisers.reduce(
              (s, ta) => s + (ta.activeCampaigns || 0),
              0
            ),
            hasRedCampaignAlerts: transformedAdvertisers.some(
              (ta) => ta.hasRedCampaignAlerts
            ),
            redCampaignAlerts: transformedAdvertisers
              .filter((ta) => ta.hasRedCampaignAlerts)
              .flatMap((ta) => ta.redCampaignAlerts || []),
            sumResults: sumMainActionResults({
              advertisers: transformedAdvertisers,
            }),
            sumOptimizations: transformedAdvertisers.reduce(
              (s, ta) => s + (ta.optimizationsApplied || 0),
              0
            ),
            sumAiCreatives: transformedAdvertisers.reduce(
              (s, ta) => s + (ta.aiCreativesLast30Days || 0),
              0
            ),
            representativeAdvertiser: first?.advertiser,
            sharedLastLogin: first?.lastLogin ?? null,
            sharedTotalLoginsLast30Days: first?.totalLoginsLast30Days ?? 0,
            sharedActiveUsers: first?.activeUsers ?? 0,
            agencyMrr: first?.mrr ?? 0,
            representativeNps: first?.npsRating ?? null,
          };
        }
        agencyBadgeDetails = getAgencyBadgeDetailsFromAdvertisers({
          advertisers: transformedAdvertisers,
        });
        agencyCancellationData = getAgencyCancellationFromAdvertisers({
          advertisers: transformedAdvertisers,
        });
      }

      return {
        ...agencyGroup,
        advertisers: transformedAdvertisers,
        agencyAverageHealthScore,
        isAgencyHealthLoading,
        agencyRowMetrics,
        agencyBadgeDetails,
        agencyCancellationData,
      };
    });
  }, [groupedClients]);

  const clientsKeys = displayedClients?.map((client) => client.id) || [];

  const onLoginAsClient = async ({ email, loadingId }) => {
    if (!email || !REGEX_EMAIL.test(email)) {
      SimpleAlert({
        title: t(ERROR),
        text: t(OWNER_LOGIN_AS_ERROR_INVALID_EMAIL),
        icon: ALERT_ICON_TYPE_ERROR,
      });
      return;
    }

    if (loadingId) {
      setLoginAsClientLoadingId(loadingId);
    }

    dispatch(setShowBackdrop(true));
    try {
      const response = await dispatch(
        startCreateOwnerLoginAsClientLink({ email })
      );

      if (!response?.ok || !response?.data?.hash) {
        SimpleAlert({
          title: t(ERROR),
          text: t(getOwnerLoginAsCreateErrorMessageKey(response?.code)),
          icon: ALERT_ICON_TYPE_ERROR,
        });
        return;
      }

      window.open(
        buildOwnerLoginAsClientUrl({ hash: response.data.hash }),
        "_blank"
      );
    } finally {
      dispatch(setShowBackdrop(false));
      if (loadingId) {
        setLoginAsClientLoadingId(null);
      }
    }
  };

  const getAdvertiserDisplayName = (name = "") =>
    String(name).split("\n")[0] || name;

  const onSelectAdvertiserAnnotationTarget = ({ client }) => {
    onSelectAnnotationTarget({
      advertiserID: client.id,
      displayName: getAdvertiserDisplayName(client.name),
      isAgency: false,
    });
  };

  const onSelectAgencyAnnotationTarget = ({ agencyID, agencyName }) => {
    onSelectAnnotationTarget({
      advertiserID: agencyID,
      displayName: agencyName,
      isAgency: true,
    });
  };

  const onStopRowClickPropagation = (event) => {
    event.stopPropagation();
  };

  // Helper function to render an advertiser row
  const renderAdvertiserRow = ({
    client,
    isLastRow,
    indentLevel = 0,
    agencyID = null,
    rowType = CLIENTS_TABLE_ROW_TYPES.SOLO_ADVERTISER,
    showAddonSpendFeeStar = true,
    isAppSumo = client?.advertiser?.isAppSumo,
    discoverySourceKey = null,
  }) => {
    const advertiserRecurrent =
      client?.advertiser?.recurrent ||
      client?.advertiser?.subscriptionPlanInfo?.recurrent ||
      "monthly";
    const advertiserAgencyID = agencyID || client?.advertiser?.agencyID;
    const isSoloAdvertiserRow =
      rowType === CLIENTS_TABLE_ROW_TYPES.SOLO_ADVERTISER;
    const isAgencyAdvertiserRow =
      rowType === CLIENTS_TABLE_ROW_TYPES.AGENCY_ADVERTISER;
    const advertiserUserInfo = getAdvertiserUserSubtitle(
      client.advertiser.users,
      client.searchMatchedEmail
    );
    const primaryAdvertiserEmail = getPrimaryAdvertiserEmail({
      users: client.advertiser.users,
      searchMatchedEmail: client.searchMatchedEmail,
    });
    const showLoginAsClient =
      Boolean(primaryAdvertiserEmail) &&
      (isSoloAdvertiserRow || isAgencyAdvertiserRow);
    const isLoginAsClientLoading = loginAsClientLoadingId === client.id;
    const loginAsClientAriaLabel = t(TABLE_ADMIN_LOGIN_AS_CLIENT_BUTTON);
    const hasAddonSpendFee = client?.advertiser?.hasAddonSpendFee === true;
    const showPriorityLabel =
      isSoloAdvertiserRow && isPriorityClient(client?.advertiser?.creationTime);

    const resultsValue = client?.metrics?.totals?.mainAction;
    const hasResultsValue =
      typeof resultsValue === "number" && !Number.isNaN(resultsValue);
    const compactResultsValue = hasResultsValue
      ? numberCompactFormat(resultsValue)
      : "-";
    const advertiserCanAddCredits =
      client?.advertiser?.typeOfPlans === PLAN_TYPE_CREDITS;
    const kpiCellValue = getKpiCellValue({
      kpiSummary: client?.kpiSummary || client?.advertiser?.kpiSummary,
      t,
    });
    const cpaCurrencyExceedanceByCurrency = getCpaCurrencyExceedanceByCurrency({
      kpiSummary: client?.kpiSummary || client?.advertiser?.kpiSummary,
      metricsTotals: client?.metrics?.totals,
    });
    const advertiserActionsOptions = [];

    if (isSoloAdvertiserRow && isValidAgencyID(advertiserAgencyID)) {
      advertiserActionsOptions.push({
        label: TABLE_ADMIN_ADD_ADVERTISER_BUTTON,
        onClick: () => onOpenAddAdvertiserModal(advertiserAgencyID),
      });
    }
    if (
      isSoloAdvertiserRow &&
      advertiserCanAddCredits &&
      isValidAgencyID(advertiserAgencyID)
    ) {
      advertiserActionsOptions.push({
        label: TABLE_ADMIN_ADD_CREDITS_BUTTON,
        onClick: () => onOpenAddCreditsModal(advertiserAgencyID),
      });
    }

    if (
      isSoloAdvertiserRow &&
      hasSubscriptionPlanRow(client?.advertiser) &&
      isValidAgencyID(advertiserAgencyID)
    ) {
      advertiserActionsOptions.push({
        label:
          client.advertiser.isPlanActive === true
            ? TABLE_ADMIN_DEACTIVATE_PLAN_BUTTON
            : TABLE_ADMIN_ACTIVATE_PLAN_BUTTON,
        onClick: () =>
          onSwitchAgencyPlanActive({
            agencyID: advertiserAgencyID,
            isDeactivation: client.advertiser.isPlanActive === true,
          }),
      });
    }

    if (
      (isSoloAdvertiserRow || isAgencyAdvertiserRow) &&
      isValidAgencyID(advertiserAgencyID)
    ) {
      advertiserActionsOptions.push({
        label: TABLE_ADMIN_MARK_DEMO_BUTTON,
        onClick: () => onMarkAgencyAsDemo({ agencyID: advertiserAgencyID }),
      });
    }

    const rawUsersForEmail = client?.advertiser?.users;
    if (isSoloAdvertiserRow && hasUsersWithEmail(rawUsersForEmail)) {
      advertiserActionsOptions.push({
        label: TABLE_ADMIN_SEND_EMAIL_BUTTON,
        onClick: () =>
          onOpenSendEmailModal({
            users: sortUsersByRolePriority(rawUsersForEmail),
            contextTitle: client.name,
          }),
      });
    }

    if (client?.id && (isSoloAdvertiserRow || isAgencyAdvertiserRow)) {
      advertiserActionsOptions.push({
        label: TABLE_ADMIN_MODIFY_KPI_CPA_BUTTON,
        onClick: () => onOpenModifyKpiCpaModal({ client }),
      });
    }

    const unreadPresentation = getAnnotationUnreadPresentation({
      readStateByScope: annotationReadStateByScope,
      advertiserID: client.id,
      isAgency: false,
    });

    return (
      <TableRow
        key={client.id}
        hover
        onClick={() => onSelectAdvertiserAnnotationTarget({ client })}
        sx={{
          cursor: "pointer",
          ...unreadPresentation.rowSx,
        }}
      >
        <TableCell
          {...commonPropsTableCellBody}
          isLastRow={isLastRow}
          showTooltip={false}
          sx={{
            paddingLeft: `${14 + indentLevel * 24}px !important`,
            width: "200px",
            minWidth: "180px",
          }}
        >
          <Box>
            {client.name.includes("\n") ? (
              <>
                {client.name.split("\n").map((name, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      mb: idx < client.name.split("\n").length - 1 ? 0.5 : 0,
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: unreadPresentation.nameTypographyWeight,
                      }}
                    >
                      {name}
                    </Typography>
                    {idx === 0 && showLoginAsClient ? (
                      <LoginAsClientIconButton
                        isLoading={isLoginAsClientLoading}
                        ariaLabel={loginAsClientAriaLabel}
                        onClick={(event) => {
                          event.stopPropagation();
                          onLoginAsClient({
                            email: primaryAdvertiserEmail,
                            loadingId: client.id,
                          });
                        }}
                      />
                    ) : null}
                    {idx === 0 && showPriorityLabel ? (
                      <PriorityChip t={t} />
                    ) : null}
                    {idx === 0 ? (
                      <AnnotationUnreadIndicator
                        hasUnread={unreadPresentation.hasUnread}
                        unreadCount={unreadPresentation.unreadCount}
                        t={t}
                      />
                    ) : null}
                    {idx === 0 &&
                    client?.advertiser?.isPendingPayment === true ? (
                      <PendingPaymentBadge t={t} />
                    ) : null}
                    {idx === 0 && hasAddonSpendFee && showAddonSpendFeeStar ? (
                      <AddOnSpendFeeStar />
                    ) : null}
                    {idx === 0 && isAppSumo ? <AppSumoChip /> : null}
                  </Box>
                ))}
                {advertiserUserInfo ? (
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      fontSize: "0.75rem",
                      display: "block",
                      mt: 0.5,
                    }}
                  >
                    {advertiserUserInfo}
                  </Typography>
                ) : null}
                {advertiserUserInfo && !isAgencyAdvertiserRow ? (
                  hasCancellationData(client?.advertiser) ? (
                    <CancellationDetailsRow
                      cancellationData={client.advertiser.cancellationData}
                      businessName={client.name}
                      onOpenModal={handleOpenCancellationModal}
                      t={t}
                    />
                  ) : (
                    <AdvertiserDetailsRow
                      recurrent={advertiserRecurrent}
                      subscriptionPlanInfo={
                        client?.advertiser?.subscriptionPlanInfo
                      }
                      typeOfPlans={
                        client.typeOfPlans ?? client?.advertiser?.typeOfPlans
                      }
                      t={t}
                    />
                  )
                ) : null}
              </>
            ) : (
              <>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: unreadPresentation.nameTypographyWeight,
                    }}
                  >
                    {client.name}
                  </Typography>
                  {showLoginAsClient ? (
                    <LoginAsClientIconButton
                      isLoading={isLoginAsClientLoading}
                      ariaLabel={loginAsClientAriaLabel}
                      onClick={(event) => {
                        event.stopPropagation();
                        onLoginAsClient({
                          email: primaryAdvertiserEmail,
                          loadingId: client.id,
                        });
                      }}
                    />
                  ) : null}
                  {showPriorityLabel ? <PriorityChip t={t} /> : null}
                  <AnnotationUnreadIndicator
                    hasUnread={unreadPresentation.hasUnread}
                    unreadCount={unreadPresentation.unreadCount}
                    t={t}
                  />
                  {client?.advertiser?.isPendingPayment === true ? (
                    <PendingPaymentBadge t={t} />
                  ) : null}
                  {hasAddonSpendFee && showAddonSpendFeeStar ? (
                    <AddOnSpendFeeStar />
                  ) : null}
                  {isAppSumo ? <AppSumoChip /> : null}
                </Box>
                {advertiserUserInfo ? (
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      fontSize: "0.75rem",
                      display: "block",
                      mt: 0.5,
                    }}
                  >
                    {advertiserUserInfo}
                  </Typography>
                ) : null}
                {advertiserUserInfo && !isAgencyAdvertiserRow ? (
                  hasCancellationData(client?.advertiser) ? (
                    <CancellationDetailsRow
                      cancellationData={client.advertiser.cancellationData}
                      businessName={client.name}
                      onOpenModal={handleOpenCancellationModal}
                      t={t}
                    />
                  ) : (
                    <AdvertiserDetailsRow
                      recurrent={advertiserRecurrent}
                      subscriptionPlanInfo={
                        client?.advertiser?.subscriptionPlanInfo
                      }
                      typeOfPlans={
                        client.typeOfPlans ?? client?.advertiser?.typeOfPlans
                      }
                      t={t}
                    />
                  )
                ) : null}
              </>
            )}
          </Box>
        </TableCell>
        <TableCell
          {...commonPropsTableCellBody}
          isLastRow={isLastRow}
          showTooltip={false}
          sx={commonSx}
        >
          {client.isLoading ? (
            <SkeletonCell width={60} />
          ) : (
            <Typography
              variant="body2"
              sx={{
                color: client.lastLogin > 7 ? "#ef4444" : "#374151",
                fontWeight: client.lastLogin > 7 ? 500 : 400,
              }}
            >
              {client.lastLogin !== null
                ? t(TABLE_ADMIN_LAST_LOGIN_DAYS, {
                    days: client.lastLogin,
                  })
                : "-"}
            </Typography>
          )}
        </TableCell>
        <TableCell
          {...commonPropsTableCellBody}
          isLastRow={isLastRow}
          showTooltip={false}
          sx={commonSx}
        >
          {client.isLoading ? (
            <SkeletonCell width={40} />
          ) : (
            <Typography
              variant="body2"
              sx={{
                color: client.totalLoginsLast30Days > 0 ? "#22c55e" : "#6b7280",
                fontWeight: 500,
              }}
            >
              {client.totalLoginsLast30Days}
            </Typography>
          )}
        </TableCell>
        <TableCell
          {...commonPropsTableCellBody}
          isLastRow={isLastRow}
          showTooltip={false}
          sx={commonSx}
        >
          {client.isLoading ? (
            <SkeletonCell width={50} />
          ) : (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  ...((client.aiCreativesLast30Days || 0) === 0 && {
                    color: "#ef4444",
                  }),
                }}
              >
                {t(
                  (client.aiCreativesLast30Days || 0) === 1
                    ? TABLE_ADMIN_CREATIVE_UTILIZED
                    : TABLE_ADMIN_CREATIVES_UTILIZED,
                  {
                    creativesUsed: client.aiCreativesLast30Days || 0,
                  }
                )}
              </Typography>
            </Box>
          )}
        </TableCell>
        <TableCell
          {...commonPropsTableCellBody}
          isLastRow={isLastRow}
          showTooltip={false}
          sx={commonSx}
        >
          {client.isLoading ? (
            <SkeletonCell width={70} />
          ) : (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {client.activeCampaigns === 1
                  ? t(TABLE_ADMIN_DASHBOARD_CAMPAIGNS_ACTIVE_SINGLE, {
                      campaignsActive: client.activeCampaigns,
                    })
                  : t(TABLE_ADMIN_DASHBOARD_CAMPAIGNS_ACTIVE, {
                      campaignsActive: client.activeCampaigns,
                    })}
              </Typography>
              {client.hasRedCampaignAlerts && (
                <CampaignAlertsErrorIndicator
                  alerts={client.redCampaignAlerts || []}
                />
              )}
            </Box>
          )}
        </TableCell>
        <TableCell
          {...commonPropsTableCellBody}
          isLastRow={isLastRow}
          showTooltip={false}
          sx={commonSx}
        >
          {client.isLoading ? (
            <SkeletonCell width={72} />
          ) : kpiCellValue ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {kpiCellValue.primaryKpi}
              </Typography>
              {kpiCellValue.targetCpaLines?.length ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.1,
                    alignItems: "flex-start",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary", fontWeight: 600 }}
                  >
                    {t(TABLE_ADMIN_DASHBOARD_TARGET_CPA)}:
                  </Typography>
                  {kpiCellValue.targetCpaLines.map((line, idx) => (
                    <Typography
                      key={idx}
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                        display: "block",
                        lineHeight: 1.2,
                      }}
                    >
                      {line}
                    </Typography>
                  ))}
                </Box>
              ) : kpiCellValue.targetCpaLabel ? (
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {kpiCellValue.targetCpaLabel}
                </Typography>
              ) : null}
            </Box>
          ) : (
            "-"
          )}
        </TableCell>
        <TableCell
          {...commonPropsTableCellBody}
          isLastRow={isLastRow}
          showTooltip={false}
          sx={commonSx}
        >
          {client.isLoading ? (
            <SkeletonCell width={70} />
          ) : (
            <Box sx={{ typography: "body2", fontWeight: 500 }}>
              <ValueMultipleCurrenciesHideZero
                value={client?.metrics?.totals?.cpa}
                exceededByCurrency={cpaCurrencyExceedanceByCurrency}
              />
            </Box>
          )}
        </TableCell>
        <TableCell
          {...commonPropsTableCellBody}
          isLastRow={isLastRow}
          showTooltip={false}
          sx={commonSx}
        >
          {client.isLoading ? (
            <SkeletonCell width={80} />
          ) : (
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                whiteSpace: "pre-line",
              }}
            >
              <ValueMultipleCurrenciesHideZero
                value={client?.metrics?.totals?.spend}
              />
            </Typography>
          )}
        </TableCell>
        <TableCell
          {...commonPropsTableCellBody}
          isLastRow={isLastRow}
          showTooltip={false}
          sx={commonSx}
        >
          {client.isLoading ? (
            <SkeletonCell width={50} />
          ) : (
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {numberDecimal(client?.metrics?.totals?.roas || 0)}x
            </Typography>
          )}
        </TableCell>
        <TableCell
          {...commonPropsTableCellBody}
          showTooltip={false}
          isLastRow={isLastRow}
          sx={commonSx}
        >
          {client.isLoading ? (
            <SkeletonCell width={50} />
          ) : (
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {compactResultsValue}
            </Typography>
          )}
        </TableCell>
        <TableCell
          {...commonPropsTableCellBody}
          isLastRow={isLastRow}
          showTooltip={false}
          sx={commonSx}
        >
          {client.isLoading ? (
            <SkeletonCell width={40} />
          ) : (
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {client.optimizationsApplied}
            </Typography>
          )}
        </TableCell>
        <TableCell
          {...commonPropsTableCellBody}
          isLastRow={isLastRow}
          showTooltip={false}
          sx={commonSx}
        >
          {client.isLoading ? (
            <SkeletonCell width={90} />
          ) : isSoloAdvertiserRow ? (
            <Typography
              variant="body2"
              sx={getRenewDateTypographySx({
                renewDate: client?.advertiser?.subscriptionPlanInfo?.renewDate,
              })}
            >
              {formatRenewDateCell({
                renewDate: client?.advertiser?.subscriptionPlanInfo?.renewDate,
                t,
              })}
            </Typography>
          ) : (
            "-"
          )}
        </TableCell>
        <TableCell
          {...commonPropsTableCellBody}
          isLastRow={isLastRow}
          showTooltip={false}
          sx={commonSx}
        >
          {client.isLoading ? (
            <SkeletonCell width={50} />
          ) : (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Users size={16} color="#6b7280" />
              <Typography variant="body2">{client.activeUsers}</Typography>
            </Box>
          )}
        </TableCell>
        <TableCell
          {...commonPropsTableCellBody}
          isLastRow={isLastRow}
          showTooltip={false}
          sx={commonSx}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              ${client.mrr.toLocaleString()}
            </Typography>
          </Box>
        </TableCell>
        <TableCell
          {...commonPropsTableCellBody}
          isLastRow={isLastRow}
          showTooltip={false}
          sx={commonSx}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                color:
                  client.advertiser.subscriptionPlan !== WITH_OUT_PLAN
                    ? "primary.main"
                    : "text.secondary",
              }}
            >
              {client.advertiser.subscriptionPlan &&
              client.advertiser.subscriptionPlan !== WITH_OUT_PLAN
                ? client.advertiser.subscriptionPlan.charAt(0).toUpperCase() +
                  client.advertiser.subscriptionPlan.slice(1).toLowerCase()
                : t(WITH_OUT_PLAN)}
            </Typography>
            {client.advertiser.subscriptionPlan !== WITH_OUT_PLAN && (
              <Typography
                variant="caption"
                sx={{
                  color:
                    client.advertiser.isPlanActive === true
                      ? "success.main"
                      : "error.main",
                  fontWeight: "medium",
                }}
              >
                {client.advertiser.isPlanActive === true
                  ? t(ACTIVE)
                  : t(INACTIVE)}
              </Typography>
            )}
          </Box>
        </TableCell>
        <TableCell
          {...commonPropsTableCellBody}
          isLastRow={isLastRow}
          showTooltip={false}
          sx={commonSx}
        >
          {client.isLoading ? (
            <SkeletonCell width={30} />
          ) : (
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {client.npsRating === 0 ? "-" : client.npsRating}
            </Typography>
          )}
        </TableCell>
        <TableCell
          {...commonPropsTableCellBody}
          isLastRow={isLastRow}
          showTooltip={false}
          sx={commonSx}
        >
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {getDiscoverySourceDisplay({ discoverySourceKey, t })}
          </Typography>
        </TableCell>
        <TableCell
          {...commonPropsTableCellBody}
          isLastRow={isLastRow}
          showTooltip={false}
          sx={commonSx}
          onClick={onStopRowClickPropagation}
        >
          {(() => {
            const hasActionsMenu = advertiserActionsOptions.length > 0;
            const showPlanSwitchLoader =
              hasActionsMenu &&
              agencyActionsBusyAgencyID &&
              agencyID &&
              agencyActionsBusyAgencyID === agencyID;
            if (showPlanSwitchLoader) {
              return (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: 36,
                    width: "100%",
                  }}
                >
                  <CircularProgress size={24} thickness={4} />
                </Box>
              );
            }
            if (hasActionsMenu) {
              return (
                <ButtonMenu
                  mode="button"
                  colorButton="secondary"
                  variantButton="outlined"
                  textButton={TABLE_ADMIN_ACTIONS_BUTTON}
                  options={advertiserActionsOptions}
                  disabled={Boolean(
                    agencyActionsBusyAgencyID &&
                      agencyActionsBusyAgencyID !== agencyID
                  )}
                />
              );
            }
            return "-";
          })()}
        </TableCell>
      </TableRow>
    );
  };

  return (
    <>
      <Box
        ref={tableWrapperRef}
        sx={{
          mb: 4,
          width: "100%",
          minWidth: 0,
        }}
      >
        <TableContainer
          loading={isLoading}
          keys={clientsKeys}
          page={0}
          onPageChange={() => {}}
          rowsPerPage={10}
          hasPagination={false}
          sx={{
            "& .MuiTable-root": {
              position: "relative",
            },
          }}
        >
          <TableHead
            ref={tableHeadRef}
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 10,
              backgroundColor: "#fff",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            }}
          >
            <TableRow>
              <TableCell
                {...commonPropsTableCellHeader}
                sx={{
                  paddingLeft: "14px !important",
                  width: "200px",
                  minWidth: "180px",
                  backgroundColor: "#fff",
                }}
              >
                {t(TABLE_ADMIN_DASHBOARD_CLIENT)}
              </TableCell>
              <TableCell
                {...commonPropsTableCellHeader}
                sx={{ ...commonSx, backgroundColor: "#fff" }}
              >
                {t(TABLE_ADMIN_DASHBOARD_LAST_LOGIN)}
              </TableCell>
              <TableCell
                {...commonPropsTableCellHeader}
                sx={{ ...commonSx, backgroundColor: "#fff" }}
              >
                {t(TABLE_ADMIN_DASHBOARD_LOGINS)}
              </TableCell>
              <TableCell
                {...commonPropsTableCellHeader}
                sx={{ ...commonSx, backgroundColor: "#fff" }}
              >
                {t(TABLE_ADMIN_DASHBOARD_AI_CREATIVES_LAST_30_DAYS)}
              </TableCell>
              <TableCell
                {...commonPropsTableCellHeader}
                sx={{ ...commonSx, backgroundColor: "#fff" }}
              >
                {t(TABLE_ADMIN_DASHBOARD_CAMPAIGNS)}
              </TableCell>
              <TableCell
                {...commonPropsTableCellHeader}
                sx={{ ...commonSx, backgroundColor: "#fff" }}
              >
                {t(TABLE_ADMIN_DASHBOARD_KPI)}
              </TableCell>
              <TableCell
                {...commonPropsTableCellHeader}
                sx={{ ...commonSx, backgroundColor: "#fff" }}
              >
                {t(TABLE_ADMIN_DASHBOARD_CPA)}
              </TableCell>
              <TableCell
                {...commonPropsTableCellHeader}
                sx={{ ...commonSx, backgroundColor: "#fff" }}
              >
                {t(TABLE_ADMIN_DASHBOARD_SPEND)}
              </TableCell>
              <TableCell
                {...commonPropsTableCellHeader}
                sx={{ ...commonSx, backgroundColor: "#fff" }}
              >
                {t(TABLE_ADMIN_DASHBOARD_ROAS)}
              </TableCell>
              <TableCell
                {...commonPropsTableCellHeader}
                sx={{ ...commonSx, backgroundColor: "#fff" }}
              >
                {t(TABLE_ADMIN_DASHBOARD_RESULTS)}
              </TableCell>
              <TableCell
                {...commonPropsTableCellHeader}
                sx={{ ...commonSx, backgroundColor: "#fff" }}
              >
                {t(TABLE_ADMIN_DASHBOARD_AUTOMATION)}
              </TableCell>
              <TableCell
                {...commonPropsTableCellHeader}
                sx={{ ...commonSx, backgroundColor: "#fff" }}
              >
                {t(TABLE_ADMIN_DASHBOARD_RENEW_DATE)}
              </TableCell>
              <TableCell
                {...commonPropsTableCellHeader}
                sx={{ ...commonSx, backgroundColor: "#fff" }}
              >
                {t(TABLE_ADMIN_DASHBOARD_USERS)}
              </TableCell>
              <TableCell
                {...commonPropsTableCellHeader}
                sx={{ ...commonSx, backgroundColor: "#fff" }}
              >
                {t(TABLE_ADMIN_DASHBOARD_MRR)}
              </TableCell>
              <TableCell
                {...commonPropsTableCellHeader}
                sx={{ ...commonSx, backgroundColor: "#fff" }}
              >
                {t(TABLE_ADMIN_DASHBOARD_PLAN)}
              </TableCell>
              <TableCell
                {...commonPropsTableCellHeader}
                sx={{ ...commonSx, backgroundColor: "#fff" }}
              >
                {t(TABLE_ADMIN_DASHBOARD_NPS)}
              </TableCell>
              <TableCell
                {...commonPropsTableCellHeader}
                sx={{ ...commonSx, backgroundColor: "#fff" }}
              >
                {t(TABLE_ADMIN_DASHBOARD_DISCOVERY_SOURCE)}
              </TableCell>
              <TableCell
                {...commonPropsTableCellHeader}
                sx={{ ...commonSx, backgroundColor: "#fff" }}
              >
                {t(COLUMN_NAME_ACTIONS)}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {transformedGroupedClients.map((agencyGroup, agencyIndex) => {
              const {
                agencyRowMetrics,
                agencyBadgeDetails,
                agencyCancellationData,
              } = agencyGroup;
              const isAgencyExpanded = expandedAgencies.has(
                agencyGroup.agencyID
              );
              const isLastAgency =
                agencyIndex === transformedGroupedClients.length - 1;
              const totalAdvertisers = agencyGroup.advertisers.length;
              const agencyName =
                agencyGroup.agencyName ||
                agencyGroup.advertisers[0]?.name ||
                agencyGroup.agencyID;
              const agencyHasAddonSpendFee = agencyGroup.advertisers.some(
                (c) => c?.advertiser?.hasAddonSpendFee
              );
              const agencyHasPendingPayment = agencyGroup.advertisers.some(
                (c) => c?.advertiser?.isPendingPayment === true
              );
              const agencyRootAdvertiser = agencyGroup.advertisers.find(
                (c) => c.id === agencyGroup.agencyID
              );
              const showAgencyPriorityLabel = isPriorityClient(
                agencyRootAdvertiser?.advertiser?.creationTime
              );
              const agencyActionsOptions = [];

              if (isValidAgencyID(agencyGroup.agencyID)) {
                agencyActionsOptions.push({
                  label: TABLE_ADMIN_ADD_ADVERTISER_BUTTON,
                  onClick: () => onOpenAddAdvertiserModal(agencyGroup.agencyID),
                });
              }
              if (
                isValidAgencyID(agencyGroup.agencyID) &&
                agencyGroupHasCreditsPlan(agencyGroup)
              ) {
                agencyActionsOptions.push({
                  label: TABLE_ADMIN_ADD_CREDITS_BUTTON,
                  onClick: () => onOpenAddCreditsModal(agencyGroup.agencyID),
                });
              }

              const representativePlanAdvertiser = agencyGroup.advertisers.find(
                (c) => hasSubscriptionPlanRow(c?.advertiser)
              )?.advertiser;
              if (
                representativePlanAdvertiser &&
                isValidAgencyID(agencyGroup.agencyID)
              ) {
                agencyActionsOptions.push({
                  label:
                    representativePlanAdvertiser.isPlanActive === true
                      ? TABLE_ADMIN_DEACTIVATE_PLAN_BUTTON
                      : TABLE_ADMIN_ACTIVATE_PLAN_BUTTON,
                  onClick: () =>
                    onSwitchAgencyPlanActive({
                      agencyID: agencyGroup.agencyID,
                      isDeactivation:
                        representativePlanAdvertiser.isPlanActive === true,
                    }),
                });
              }

              const mergedUsersForEmail = mergeUsersForAgencyEmail(agencyGroup);
              const agencyLoginEmail = getPrimaryAdvertiserEmail({
                users: mergedUsersForEmail,
              });
              const showAgencyLoginAsClient = Boolean(agencyLoginEmail);
              const isAgencyLoginAsClientLoading =
                loginAsClientLoadingId === agencyGroup.agencyID;
              const loginAsClientAriaLabel = t(
                TABLE_ADMIN_LOGIN_AS_CLIENT_BUTTON
              );

              if (hasUsersWithEmail(mergedUsersForEmail)) {
                agencyActionsOptions.push({
                  label: TABLE_ADMIN_SEND_EMAIL_BUTTON,
                  onClick: () =>
                    onOpenSendEmailModal({
                      users: mergedUsersForEmail,
                      contextTitle: agencyName,
                    }),
                });
              }

              const agencyUnreadPresentation = getAnnotationUnreadPresentation({
                readStateByScope: annotationReadStateByScope,
                advertiserID: agencyGroup.agencyID,
                isAgency: true,
                variant: "agency",
              });

              // If agency has only one advertiser, render it as a normal row (no expandable)
              if (totalAdvertisers === 1) {
                const client = agencyGroup.advertisers[0];
                const isLastRow = isLastAgency;
                return renderAdvertiserRow({
                  client,
                  isLastRow,
                  indentLevel: 0,
                  agencyID: client.advertiser?.agencyID,
                  rowType: CLIENTS_TABLE_ROW_TYPES.SOLO_ADVERTISER,
                  isAppSumo: agencyGroup.agencyIsAppSumo,
                  discoverySourceKey: agencyGroup.agencyDiscoverySource,
                });
              }

              // Agency with multiple advertisers - render with expandable row
              return (
                <>
                  {/* Agency Row */}
                  <TableRow
                    key={`agency-${agencyGroup.agencyID}`}
                    onClick={() =>
                      onSelectAgencyAnnotationTarget({
                        agencyID: agencyGroup.agencyID,
                        agencyName,
                      })
                    }
                    sx={{
                      cursor: "pointer",
                      ...agencyUnreadPresentation.rowSx,
                    }}
                  >
                    <TableCell
                      {...commonPropsTableCellBody}
                      isLastRow={isLastAgency && !isAgencyExpanded}
                      showTooltip={false}
                      sx={{
                        paddingLeft: "14px !important",
                        width: "200px",
                        minWidth: "180px",
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <IconButton
                          size="small"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleToggleAgency(agencyGroup.agencyID);
                          }}
                          sx={{
                            padding: "4px",
                            "&:hover": {
                              backgroundColor: "rgba(0, 0, 0, 0.04)",
                            },
                          }}
                        >
                          {isAgencyExpanded ? (
                            <ExpandMoreIcon fontSize="small" />
                          ) : (
                            <ChevronRightIcon fontSize="small" />
                          )}
                        </IconButton>
                        <Box
                          sx={{ display: "flex", flexDirection: "column" }}
                          onClick={onStopRowClickPropagation}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.75,
                            }}
                          >
                            <EditableAgencyName
                              agencyID={agencyGroup.agencyID}
                              value={agencyName}
                            />
                            {showAgencyLoginAsClient ? (
                              <LoginAsClientIconButton
                                isLoading={isAgencyLoginAsClientLoading}
                                ariaLabel={loginAsClientAriaLabel}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  onLoginAsClient({
                                    email: agencyLoginEmail,
                                    loadingId: agencyGroup.agencyID,
                                  });
                                }}
                              />
                            ) : null}
                            {showAgencyPriorityLabel ? (
                              <PriorityChip t={t} />
                            ) : null}
                            <AnnotationUnreadIndicator
                              hasUnread={agencyUnreadPresentation.hasUnread}
                              unreadCount={agencyUnreadPresentation.unreadCount}
                              t={t}
                            />
                            {agencyHasPendingPayment ? (
                              <PendingPaymentBadge t={t} />
                            ) : null}
                            {agencyHasAddonSpendFee ? (
                              <AddOnSpendFeeStar />
                            ) : null}
                            {agencyGroup.agencyIsAppSumo ? (
                              <AppSumoChip />
                            ) : null}
                          </Box>
                          <Typography
                            variant="caption"
                            sx={{ color: "#9ca3af", fontSize: "0.75rem" }}
                          >
                            {totalAdvertisers === 1
                              ? t(TOTAL_ADVERTISER_COUNT, {
                                  value: totalAdvertisers,
                                })
                              : t(TOTAL_ADVERTISERS_COUNT, {
                                  value: totalAdvertisers,
                                })}
                          </Typography>
                          {agencyCancellationData ? (
                            <CancellationDetailsRow
                              cancellationData={agencyCancellationData}
                              businessName={agencyName}
                              onOpenModal={handleOpenCancellationModal}
                              t={t}
                              sx={{
                                mt: 0.2,
                              }}
                            />
                          ) : agencyBadgeDetails ? (
                            <AdvertiserDetailsRow
                              recurrent={agencyBadgeDetails.recurrent}
                              subscriptionPlanInfo={
                                agencyBadgeDetails.subscriptionPlanInfo
                              }
                              typeOfPlans={agencyBadgeDetails.typeOfPlans}
                              t={t}
                              sx={{
                                mt: 0.2,
                              }}
                            />
                          ) : null}
                        </Box>
                      </Box>
                    </TableCell>
                    {(() => {
                      const arm = agencyRowMetrics;
                      const loading = arm?.isLoading;
                      const rep =
                        arm && !arm.isLoading
                          ? arm.representativeAdvertiser
                          : null;

                      const compactAgencyResults =
                        arm &&
                        !arm.isLoading &&
                        typeof arm.sumResults === "number" &&
                        !Number.isNaN(arm.sumResults)
                          ? numberCompactFormat(arm.sumResults)
                          : "-";

                      return (
                        <>
                          <TableCell
                            {...commonPropsTableCellBody}
                            isLastRow={false}
                            showTooltip={false}
                            sx={commonSx}
                          >
                            {loading ? (
                              <SkeletonCell width={60} />
                            ) : (
                              <Typography
                                variant="body2"
                                sx={{
                                  color:
                                    arm.sharedLastLogin > 7
                                      ? "#ef4444"
                                      : "#374151",
                                  fontWeight:
                                    arm.sharedLastLogin > 7 ? 500 : 400,
                                }}
                              >
                                {arm.sharedLastLogin !== null
                                  ? t(TABLE_ADMIN_LAST_LOGIN_DAYS, {
                                      days: arm.sharedLastLogin,
                                    })
                                  : "-"}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell
                            {...commonPropsTableCellBody}
                            isLastRow={false}
                            showTooltip={false}
                            sx={commonSx}
                          >
                            {loading ? (
                              <SkeletonCell width={40} />
                            ) : (
                              <Typography
                                variant="body2"
                                sx={{
                                  color:
                                    arm.sharedTotalLoginsLast30Days > 0
                                      ? "#22c55e"
                                      : "#6b7280",
                                  fontWeight: 500,
                                }}
                              >
                                {arm.sharedTotalLoginsLast30Days}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell
                            {...commonPropsTableCellBody}
                            isLastRow={false}
                            showTooltip={false}
                            sx={commonSx}
                          >
                            {loading ? (
                              <SkeletonCell width={50} />
                            ) : (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 500,
                                    ...((arm.sumAiCreatives || 0) === 0 && {
                                      color: "#ef4444",
                                    }),
                                  }}
                                >
                                  {t(
                                    (arm.sumAiCreatives || 0) === 1
                                      ? TABLE_ADMIN_CREATIVE_UTILIZED
                                      : TABLE_ADMIN_CREATIVES_UTILIZED,
                                    {
                                      creativesUsed: arm.sumAiCreatives || 0,
                                    }
                                  )}
                                </Typography>
                              </Box>
                            )}
                          </TableCell>
                          <TableCell
                            {...commonPropsTableCellBody}
                            isLastRow={false}
                            showTooltip={false}
                            sx={commonSx}
                          >
                            {loading ? (
                              <SkeletonCell width={70} />
                            ) : (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 500 }}
                                >
                                  {arm.sumCampaigns === 1
                                    ? t(
                                        TABLE_ADMIN_DASHBOARD_CAMPAIGNS_ACTIVE_SINGLE,
                                        {
                                          campaignsActive: arm.sumCampaigns,
                                        }
                                      )
                                    : t(
                                        TABLE_ADMIN_DASHBOARD_CAMPAIGNS_ACTIVE,
                                        {
                                          campaignsActive: arm.sumCampaigns,
                                        }
                                      )}
                                </Typography>
                                {arm.hasRedCampaignAlerts ? (
                                  <CampaignAlertsErrorIndicator
                                    alerts={arm.redCampaignAlerts || []}
                                  />
                                ) : null}
                              </Box>
                            )}
                          </TableCell>
                          <TableCell
                            {...commonPropsTableCellBody}
                            isLastRow={false}
                            showTooltip={false}
                            sx={commonSx}
                          >
                            -
                          </TableCell>
                          <TableCell
                            {...commonPropsTableCellBody}
                            isLastRow={false}
                            showTooltip={false}
                            sx={commonSx}
                          >
                            {loading ? (
                              <SkeletonCell width={70} />
                            ) : (
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 500,
                                  whiteSpace: "pre-line",
                                }}
                              >
                                <ValueMultiCurrencyCPA
                                  value={arm.cpaAverageByCurrency}
                                />
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell
                            {...commonPropsTableCellBody}
                            isLastRow={false}
                            showTooltip={false}
                            sx={commonSx}
                          >
                            {loading ? (
                              <SkeletonCell width={80} />
                            ) : (
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 500,
                                  whiteSpace: "pre-line",
                                }}
                              >
                                <ValueMultipleCurrenciesHideZero
                                  value={arm.spendByCurrency}
                                />
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell
                            {...commonPropsTableCellBody}
                            isLastRow={false}
                            showTooltip={false}
                            sx={commonSx}
                          >
                            {loading ? (
                              <SkeletonCell width={50} />
                            ) : (
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 500 }}
                              >
                                {arm.roasAverage != null
                                  ? `${numberDecimal(arm.roasAverage)}x`
                                  : "-"}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell
                            {...commonPropsTableCellBody}
                            showTooltip={false}
                            isLastRow={false}
                            sx={commonSx}
                          >
                            {loading ? (
                              <SkeletonCell width={50} />
                            ) : (
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 500 }}
                              >
                                {compactAgencyResults}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell
                            {...commonPropsTableCellBody}
                            isLastRow={false}
                            showTooltip={false}
                            sx={commonSx}
                          >
                            {loading ? (
                              <SkeletonCell width={40} />
                            ) : (
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 500 }}
                              >
                                {arm.sumOptimizations}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell
                            {...commonPropsTableCellBody}
                            isLastRow={isLastAgency && !isAgencyExpanded}
                            showTooltip={false}
                            sx={commonSx}
                          >
                            {loading ? (
                              <SkeletonCell width={90} />
                            ) : (
                              <Typography
                                variant="body2"
                                sx={getRenewDateTypographySx({
                                  renewDate:
                                    agencyBadgeDetails?.subscriptionPlanInfo
                                      ?.renewDate,
                                })}
                              >
                                {formatRenewDateCell({
                                  renewDate:
                                    agencyBadgeDetails?.subscriptionPlanInfo
                                      ?.renewDate,
                                  t,
                                })}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell
                            {...commonPropsTableCellBody}
                            isLastRow={false}
                            showTooltip={false}
                            sx={commonSx}
                          >
                            {loading ? (
                              <SkeletonCell width={50} />
                            ) : (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <Users size={16} color="#6b7280" />
                                <Typography variant="body2">
                                  {arm.sharedActiveUsers}
                                </Typography>
                              </Box>
                            )}
                          </TableCell>
                          <TableCell
                            {...commonPropsTableCellBody}
                            isLastRow={false}
                            showTooltip={false}
                            sx={commonSx}
                          >
                            {loading ? (
                              <SkeletonCell width={56} />
                            ) : (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 500 }}
                                >
                                  ${arm.agencyMrr.toLocaleString()}
                                </Typography>
                              </Box>
                            )}
                          </TableCell>
                          <TableCell
                            {...commonPropsTableCellBody}
                            isLastRow={false}
                            showTooltip={false}
                            sx={commonSx}
                          >
                            {loading ? (
                              <SkeletonCell width={80} />
                            ) : (
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: 0.5,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 500,
                                    color:
                                      rep?.subscriptionPlan !== WITH_OUT_PLAN
                                        ? "primary.main"
                                        : "text.secondary",
                                  }}
                                >
                                  {rep?.subscriptionPlan &&
                                  rep.subscriptionPlan !== WITH_OUT_PLAN
                                    ? rep.subscriptionPlan
                                        .charAt(0)
                                        .toUpperCase() +
                                      rep.subscriptionPlan
                                        .slice(1)
                                        .toLowerCase()
                                    : t(WITH_OUT_PLAN)}
                                </Typography>
                                {rep?.subscriptionPlan &&
                                  rep.subscriptionPlan !== WITH_OUT_PLAN && (
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color:
                                          rep.isPlanActive === true
                                            ? "success.main"
                                            : "error.main",
                                        fontWeight: "medium",
                                      }}
                                    >
                                      {rep.isPlanActive === true
                                        ? t(ACTIVE)
                                        : t(INACTIVE)}
                                    </Typography>
                                  )}
                              </Box>
                            )}
                          </TableCell>
                          <TableCell
                            {...commonPropsTableCellBody}
                            isLastRow={false}
                            showTooltip={false}
                            sx={commonSx}
                          >
                            {loading ? (
                              <SkeletonCell width={30} />
                            ) : (
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 500 }}
                              >
                                {arm.representativeNps === 0 ||
                                arm.representativeNps == null
                                  ? "-"
                                  : arm.representativeNps}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell
                            {...commonPropsTableCellBody}
                            isLastRow={false}
                            showTooltip={false}
                            sx={commonSx}
                          >
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 500 }}
                            >
                              {getDiscoverySourceDisplay({
                                discoverySourceKey:
                                  agencyGroup.agencyDiscoverySource,
                                t,
                              })}
                            </Typography>
                          </TableCell>
                        </>
                      );
                    })()}
                    <TableCell
                      {...commonPropsTableCellBody}
                      isLastRow={isLastAgency && !isAgencyExpanded}
                      showTooltip={false}
                      sx={commonSx}
                      onClick={onStopRowClickPropagation}
                    >
                      {(() => {
                        const agencyRowID = agencyGroup.agencyID;
                        const hasAgencyActionsMenu =
                          agencyActionsOptions.length > 0;
                        const showAgencyPlanSwitchLoader =
                          hasAgencyActionsMenu &&
                          agencyActionsBusyAgencyID &&
                          agencyRowID &&
                          agencyActionsBusyAgencyID === agencyRowID;
                        if (showAgencyPlanSwitchLoader) {
                          return (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                minHeight: 36,
                                width: "100%",
                              }}
                            >
                              <CircularProgress size={24} thickness={4} />
                            </Box>
                          );
                        }
                        if (hasAgencyActionsMenu) {
                          return (
                            <ButtonMenu
                              mode="button"
                              colorButton="secondary"
                              textButton={TABLE_ADMIN_ACTIONS_BUTTON}
                              options={agencyActionsOptions}
                              disabled={Boolean(
                                agencyActionsBusyAgencyID &&
                                  agencyActionsBusyAgencyID !== agencyRowID
                              )}
                            />
                          );
                        }
                        return "-";
                      })()}
                    </TableCell>
                  </TableRow>

                  {/* Advertiser Rows (collapsible) - no Add Advertiser button in expanded rows */}
                  {isAgencyExpanded &&
                    agencyGroup.advertisers.map((client, advIndex) => {
                      const isLastAdvertiser =
                        advIndex === agencyGroup.advertisers.length - 1;
                      const isLastRow =
                        isLastAgency && isLastAdvertiser && isAgencyExpanded;
                      return renderAdvertiserRow({
                        client,
                        isLastRow,
                        indentLevel: 1,
                        agencyID: agencyGroup.agencyID,
                        rowType: CLIENTS_TABLE_ROW_TYPES.AGENCY_ADVERTISER,
                        showAddonSpendFeeStar: false,
                        isAppSumo: agencyGroup.agencyIsAppSumo,
                        discoverySourceKey: agencyGroup.agencyDiscoverySource,
                      });
                    })}
                </>
              );
            })}
            {transformedGroupedClients.length === 0 &&
              displayedClients?.map((client, index) => {
                // Fallback: if no grouped clients, render flat list
                const transformedClient =
                  transformedGroupedClients.length === 0
                    ? (() => {
                        const advertiser = client.advertiser;
                        const activeCampaignsCount =
                          advertiser.campaigns?.activeCampaigns || 0;
                        const appliedOptimizationsCount =
                          advertiser.optimizations?.appliedOptimizations || 0;
                        const activeUsersCount = advertiser.activeUsers || 0;
                        const totalLoginsLast30Days =
                          advertiser.loginMetrics?.totalLoginsLast30Days || 0;
                        const daysSinceLastLogin =
                          advertiser.loginMetrics?.daysSinceLastLogin ?? null;
                        const healthScore = advertiser.isLoading
                          ? null
                          : (advertiser.healthScore ?? null);

                        return {
                          id: client.id,
                          name: advertiser.businessName,
                          searchMatchedEmail:
                            advertiser.searchMatchedEmail ?? null,
                          isLoading: advertiser.isLoading,
                          kpiSummary:
                            advertiser.kpiSummary ||
                            advertiser.campaigns?.kpiSummary ||
                            null,
                          healthScore,
                          activeCampaigns: activeCampaignsCount,
                          metrics: advertiser.metrics,
                          lastLogin: daysSinceLastLogin,
                          totalLoginsLast30Days,
                          activeUsers: activeUsersCount,
                          optimizationsApplied: appliedOptimizationsCount,
                          npsRating: advertiser.npsRating || 0,
                          mrr: advertiser.subscriptionPlanInfo?.price || 0,
                          aiCreativesLast30Days:
                            advertiser.aiCreativesLast30Days || 0,
                          typeOfPlans: advertiser.typeOfPlans,
                          advertiser,
                        };
                      })()
                    : null;

                if (!transformedClient) return null;

                return renderAdvertiserRow({
                  client: transformedClient,
                  isLastRow: index === (displayedClients?.length || 0) - 1,
                  indentLevel: 0,
                  agencyID: transformedClient.advertiser?.agencyID,
                  rowType: CLIENTS_TABLE_ROW_TYPES.SOLO_ADVERTISER,
                });
              })}
            {hasMoreItems && (
              <TableRow
                ref={lastItemRef}
                sx={{
                  "&:hover": {
                    backgroundColor: "transparent !important",
                  },
                }}
              >
                <TableCell
                  colSpan={18}
                  align="center"
                  sx={{
                    height: "80px !important",
                    padding: "20px !important",
                    border: "none",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      width: "100%",
                      flexDirection: "column",
                      gap: 1,
                    }}
                  >
                    {isLoadingMore && (
                      <>
                        <Box
                          sx={{
                            width: "24px",
                            height: "24px",
                            border: "2px solid #f3f4f6",
                            borderTop: "2px solid #3b82f6",
                            borderRadius: "50%",
                            animation: "spin 1s linear infinite",
                            "@keyframes spin": {
                              "0%": { transform: "rotate(0deg)" },
                              "100%": { transform: "rotate(360deg)" },
                            },
                          }}
                        />
                        <Typography variant="body2" color="text.secondary">
                          {t(TABLE_ADMIN_DASHBOARD_LOADING_MORE)}
                        </Typography>
                      </>
                    )}
                    {!isLoadingMore && (
                      <Typography variant="body2" color="text.secondary">
                        {t(TABLE_ADMIN_DASHBOARD_SHOWING_CLIENTS, {
                          count: displayedClients?.length || 0,
                          total: totalClients,
                        })}
                      </Typography>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </TableContainer>

        <CancellationReasonModal
          modalOpen={modalOpen}
          onCloseModal={handleCloseCancellationModal}
          cancellationData={selectedCancellationData}
          businessName={selectedBusinessName}
        />
      </Box>

      {/* Modal Add Advertiser */}
      {addAdvertiserModalOpen && (
        <ModalAddAdvertiser
          modalOpen={addAdvertiserModalOpen}
          onCloseModal={onCloseAddAdvertiserModal}
          agencyID={addAdvertiserAgencyID}
        />
      )}
      {addCreditsModalOpen && (
        <ModalAddCredits
          modalOpen={addCreditsModalOpen}
          onCloseModal={onCloseAddCreditsModal}
          agencyID={addCreditsAgencyID}
        />
      )}
      {sendEmailModalOpen && (
        <ModalSendClientEmail
          modalOpen={sendEmailModalOpen}
          onCloseModal={onCloseSendEmailModal}
          users={sendEmailUsers}
          contextTitle={sendEmailContextTitle}
        />
      )}
      {modifyKpiCpaModalOpen && (
        <ModalModifyKpiCpa
          modalOpen={modifyKpiCpaModalOpen}
          onCloseModal={onCloseModifyKpiCpaModal}
          advertiserID={modifyKpiCpaModalClient?.id}
          businessName={modifyKpiCpaModalClient?.name}
          initialKpiSummary={modifyKpiCpaModalClient?.advertiser?.kpiSummary}
        />
      )}
    </>
  );
};

const ValueMultipleCurrenciesHideZero = ({
  value,
  exceededByCurrency = null,
}) => {
  if (!value || typeof value !== "object" || _.isEmpty(value)) return "-";

  const entries = Object.entries(value).filter(([, amount]) =>
    isMeaningfulNumericMetricValue(amount)
  );

  if (!entries.length) {
    return "-";
  }

  const hasExceedanceLookup =
    exceededByCurrency && typeof exceededByCurrency === "object";

  return (
    <Box
      component="span"
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
      }}
    >
      {entries.map(([currency, amount], idx) => {
        const curKey =
          getNormalizedCurrencyCode(currency) || String(currency).trim();
        const isExceeded =
          hasExceedanceLookup &&
          Boolean(curKey && exceededByCurrency[curKey] === true);
        return (
          <Box
            component="span"
            key={`${String(currency)}-${idx}`}
            sx={{
              display: "block",
              color: isExceeded ? "error.main" : "inherit",
              lineHeight: 1.35,
            }}
          >
            {`${numberCompactFormat(amount)} ${currency}`}
          </Box>
        );
      })}
    </Box>
  );
};

const ValueMultiCurrencyCPA = ({ value }) => {
  if (!value || typeof value !== "object" || _.isEmpty(value)) return "-";

  const formatted = Object.entries(value)
    .filter(([, amount]) => isMeaningfulNumericMetricValue(amount))
    .map(
      ([currency, amount]) =>
        `${numberMetricFormat(Number(amount))} ${currency}`
    )
    .join("\n");

  return <>{formatted || "-"}</>;
};

// Skeleton cell component for loading states
const SkeletonCell = ({ width = 60, height = 20 }) => (
  <Skeleton
    variant="text"
    width={width}
    height={height}
    sx={{ bgcolor: "grey.200" }}
  />
);

export default ClientsTable;
